// src/pages/admin/admin-comite-dashboard.tsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import { calcularEstadoTaller } from '../../utils/tallerEstadoHelper';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';

interface TallerResumen {
  id: string;
  nombre: string;
  fecha: any;
  fechaTermino?: any;
  inscritos: number;
  estado: string;
  estadoCalculado: string;
}

// Wrapper para mantener compatibilidad con Timestamps de Firestore
const calcularEstado = (fecha: any, data: any): string => {
  if (data.estado === 'cancelado') return 'cancelado';
  const fechaDate = fecha?.toDate ? fecha.toDate() : (fecha ? new Date(fecha) : null);
  if (!fechaDate) return 'sin_fecha';
  return calcularEstadoTaller(
    fechaDate,
    data.horaInicio || '00:00',
    data.horaTermino || '23:59',
    data.estado
  );
};

export default function AdminComiteDashboard() {
  const { user, loading, logout } = useUser();
  const navigate = useNavigate();
  const [talleres, setTalleres] = useState<TallerResumen[]>([]);
  const [stats, setStats] = useState({
    totalTalleres: 0,
    talleresProximos: 0,
    totalInscritos: 0
  });
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    // Validar acceso
    if (!loading && !user) {
      navigate('/');
      return;
    }

    if (!loading && user && !user.esAdminComite && user.rol !== 'ADMIN') {
      navigate('/dashboard');
      return;
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Obtener talleres recientes
        const talleresSnap = await getDocs(
          query(collection(db, 'taller'), orderBy('fecha', 'desc'), limit(5))
        );

        const talleresData: TallerResumen[] = [];
        let totalInscritos = 0;
        let talleresProximos = 0;

        for (const doc of talleresSnap.docs) {
          const data = doc.data();

          // Contar inscritos
          const inscritosSnap = await getDocs(
            query(collection(db, 'inscripcion'), where('taller_doc_id', '==', doc.id))
          );

          const inscritos = inscritosSnap.size;
          totalInscritos += inscritos;

          // Calcular estado basándose en la fecha
          const estadoCalculado = calcularEstado(
            data.fecha || data.fechaInicio,
            data
          );

          if (estadoCalculado === 'proximo') {
            talleresProximos++;
          }

          talleresData.push({
            id: doc.id,
            nombre: data.nombre,
            fecha: data.fecha || data.fechaInicio,
            fechaTermino: data.fechaTermino,
            inscritos,
            estado: data.estado || 'proximo',
            estadoCalculado
          });
        }

        // Contar total de talleres
        const allTalleresSnap = await getDocs(collection(db, 'taller'));

        setTalleres(talleresData);
        setStats({
          totalTalleres: allTalleresSnap.size,
          talleresProximos,
          totalInscritos
        });
      } catch (error) {
        console.error('Error al cargar datos:', error);
      } finally {
        setLoadingData(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  if (loading || loadingData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (!user || (!user.esAdminComite && user.rol !== 'ADMIN')) {
    return null;
  }

  const formatFecha = (fecha: any) => {
    if (!fecha) return 'Sin fecha';
    const date = fecha.toDate ? fecha.toDate() : new Date(fecha);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      <UserNav />
      <div className="page-title" style={{ background: 'linear-gradient(135deg, #0c2339 0%, #1a4a7a 100%)' }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="ipt-title text-white">Panel de Administración de Comité</h2>
              <span className="ipn-subtitle text-white-50">
                Bienvenido, {user.nombre} | Módulos: {user.modulosAcceso?.join(', ') || 'Talleres'}
              </span>
            </div>
            <div>
              <Link to="/seleccionar-rol" className="btn btn-outline-light btn-sm me-2">
                <i className="fa-solid fa-exchange-alt me-1"></i> Cambiar Rol
              </Link>
            </div>
          </div>
        </div>
      </div>

      <section className="gray-simple">
        <div className="container py-4">
          {/* Estadísticas rápidas */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body d-flex align-items-center">
                  <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                    <i className="fa-solid fa-calendar-days fa-2x text-primary"></i>
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">{stats.totalTalleres}</h3>
                    <p className="text-muted mb-0">Talleres Totales</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body d-flex align-items-center">
                  <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                    <i className="fa-solid fa-clock fa-2x text-success"></i>
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">{stats.talleresProximos}</h3>
                    <p className="text-muted mb-0">Próximos Eventos</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body d-flex align-items-center">
                  <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                    <i className="fa-solid fa-users fa-2x text-info"></i>
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">{stats.totalInscritos}</h3>
                    <p className="text-muted mb-0">Inscritos Totales</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {/* Acciones rápidas */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0">
                  <h5 className="mb-0">
                    <i className="fa-solid fa-bolt text-warning me-2"></i>
                    Acciones Rápidas
                  </h5>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-3">
                    <Link to="/create-taller" className="btn btn-primary">
                      <i className="fa-solid fa-plus me-2"></i>
                      Crear Nuevo Taller
                    </Link>
                    <Link to="/admin-talleres" className="btn btn-outline-primary">
                      <i className="fa-solid fa-list me-2"></i>
                      Ver Todos los Talleres
                    </Link>
                    <Link to="/escanear-asistencia" className="btn btn-outline-success">
                      <i className="fa-solid fa-qrcode me-2"></i>
                      Escanear Asistencia QR
                    </Link>
                    <Link to="/ver-taller" className="btn btn-outline-secondary">
                      <i className="fa-solid fa-eye me-2"></i>
                      Ver Talleres Públicos
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Talleres recientes */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fa-solid fa-calendar-check text-primary me-2"></i>
                    Talleres Recientes
                  </h5>
                  <Link to="/admin-talleres" className="btn btn-sm btn-link">
                    Ver todos <i className="fa-solid fa-arrow-right ms-1"></i>
                  </Link>
                </div>
                <div className="card-body p-0">
                  {talleres.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fa-solid fa-calendar-xmark fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No hay talleres registrados</p>
                      <Link to="/create-taller" className="btn btn-primary btn-sm">
                        Crear primer taller
                      </Link>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Taller</th>
                            <th>Fecha</th>
                            <th>Inscritos</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {talleres.map((taller) => (
                            <tr key={taller.id}>
                              <td>
                                <strong>{taller.nombre}</strong>
                              </td>
                              <td>{formatFecha(taller.fecha)}</td>
                              <td>
                                <span className="badge bg-info">{taller.inscritos}</span>
                              </td>
                              <td>
                                {taller.estadoCalculado === 'proximo' && (
                                  <span className="badge bg-primary">Próximo</span>
                                )}
                                {taller.estadoCalculado === 'en_curso' && (
                                  <span className="badge bg-success">En Curso</span>
                                )}
                                {taller.estadoCalculado === 'finalizado' && (
                                  <span className="badge bg-secondary">Finalizado</span>
                                )}
                                {taller.estadoCalculado === 'cancelado' && (
                                  <span className="badge bg-danger">Cancelado</span>
                                )}
                                {taller.estadoCalculado === 'sin_fecha' && (
                                  <span className="badge bg-warning text-dark">Sin Fecha</span>
                                )}
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <Link
                                    to={`/ver-taller-ind/${taller.id}`}
                                    className="btn btn-outline-secondary"
                                    title="Ver detalles del taller"
                                  >
                                    <i className="fa-solid fa-eye"></i>
                                  </Link>
                                  <Link
                                    to={`/edit-taller/${taller.id}`}
                                    className="btn btn-outline-primary"
                                    title="Editar taller"
                                  >
                                    <i className="fa-solid fa-pen"></i>
                                  </Link>
                                  <Link
                                    to={`/ver-taller-ind/${taller.id}#inscritos`}
                                    className="btn btn-outline-info"
                                    title="Ver lista de inscritos"
                                  >
                                    <i className="fa-solid fa-users"></i>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Ayuda */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm bg-light">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <h6 className="fw-bold mb-2">
                        <i className="fa-solid fa-circle-info text-info me-2"></i>
                        ¿Necesitas ayuda?
                      </h6>
                      <p className="text-muted mb-0 small">
                        Si tienes dudas sobre cómo usar el sistema, consulta el manual de usuario o contacta al equipo de soporte.
                      </p>
                    </div>
                    <div className="col-md-4 text-md-end mt-3 mt-md-0">
                      <button className="btn btn-outline-primary btn-sm" onClick={logout}>
                        <i className="fa-solid fa-right-from-bracket me-1"></i>
                        Cerrar Sesión
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
